import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  Users, Home, TrendingUp, FileText, Search, AlertTriangle, 
  MapPin, BarChart3, Clock, CheckCircle, Eye, ArrowRight,
  Download, RefreshCw, Filter, Activity, Shield, Zap,
  Building, Heart, ChevronRight, AlertCircle, Bell, PieChart,
  Trash2, Edit, UserPlus, XCircle, Check, Bed, Bath, Square, DollarSign, Award,
  Calendar, UserX, UserCheck, Link2, ExternalLink, Ban, Calculator, Map,
  Key, Copy
} from "lucide-react";
import { VibeTag, VIBE_TAGS, getAllVibeTagsArray } from "@/components/VibeTag";
import MexicoMap from "@/components/MexicoMap";
import { AdvancedAnalytics } from "@/components/AdvancedAnalytics";
import { PropertyDetailModal, PropertyCard } from "@/components/PropertyDetailModal";
import ValuationModule from "@/components/ValuationModule";
import TerrenoModule from "@/components/TerrenoModule";
import ColoniasManager from "@/components/ColoniasManager";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// KPI Tile Component
const KPITile = ({ title, value, subtitle, icon: Icon, trend, color = "primary" }) => (
  <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <p className="text-3xl font-bold text-slate-800 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-xl ${color === "primary" ? "bg-primary/10" : color === "blue" ? "bg-blue-100" : color === "green" ? "bg-green-100" : "bg-orange-100"}`}>
          <Icon className={`w-6 h-6 ${color === "primary" ? "text-primary" : color === "blue" ? "text-blue-600" : color === "green" ? "text-green-600" : "text-orange-600"}`} />
        </div>
      </div>
      {trend && (
        <div className="flex items-center mt-3 text-sm">
          <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
          <span className="text-green-600 font-medium">{trend}</span>
        </div>
      )}
    </CardContent>
  </Card>
);

// Alert Badge Component
const AlertBadge = ({ severity, count }) => {
  if (count === 0) return null;
  return (
    <Badge variant={severity === "critical" ? "destructive" : "outline"} className="ml-2">
      {count}
    </Badge>
  );
};

// User Row Component
const UserRow = ({ user, onView, onEdit, onDelete, onSetMasterPartner }) => {
  // Translate role to Spanish
  const getRoleLabel = (role) => {
    const labels = {
      shopper: "Comprador",
      owner: "Propietario", 
      agent: "Agente",
      admin: "Administrador"
    };
    return labels[role] || role;
  };

  return (
    <div className="flex items-center justify-between p-4 hover:bg-slate-50 rounded-lg transition-colors">
      <div className="flex items-center gap-4">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
          user.role === "shopper" ? "bg-blue-100 text-blue-600" :
          user.role === "owner" ? "bg-green-100 text-green-600" :
          user.role === "agent" ? "bg-purple-100 text-purple-600" : "bg-slate-100 text-slate-600"
        }`}>
          {user.name?.charAt(0).toUpperCase() || "?"}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium text-slate-800">{user.name}</p>
            {user.is_master_partner && (
              <Badge className="bg-amber-500 text-xs"><Award className="w-3 h-3 mr-1" /> Master Partner</Badge>
            )}
            {user.status === "disabled" && (
              <Badge variant="destructive" className="text-xs">Deshabilitado</Badge>
            )}
          </div>
          <p className="text-sm text-slate-500">{user.email}</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <Badge variant="outline" className="capitalize">{getRoleLabel(user.role)}</Badge>
        <div className="w-24">
          <div className="flex justify-between text-xs mb-1">
            <span>Onboarding</span>
            <span>{user.onboarding_progress}%</span>
          </div>
          <Progress value={user.onboarding_progress} className="h-1.5" />
        </div>
        {user.risk_level !== "low" && (
          <Badge variant={user.risk_level === "high" ? "destructive" : "secondary"} className="gap-1">
            <AlertTriangle className="w-3 h-3" />
            {user.risk_score}
          </Badge>
        )}
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => onView(user)} data-testid={`view-user-${user.id}`}>
            <Eye className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onEdit(user)} data-testid={`edit-user-${user.id}`}>
            <Edit className="w-4 h-4 text-blue-500" />
          </Button>
          {user.role === "agent" && !user.is_master_partner && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onSetMasterPartner(user)} 
              data-testid={`master-partner-${user.id}`}
              title="Configurar como Master Partner"
            >
              <Award className="w-4 h-4 text-amber-500" />
            </Button>
          )}
          {user.role !== "admin" && (
            <Button variant="ghost" size="sm" onClick={() => onDelete(user)} data-testid={`delete-user-${user.id}`}>
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Property Row Component
const PropertyRow = ({ property, onClick }) => (
  <div 
    className="flex items-center justify-between p-4 hover:bg-slate-50 rounded-lg transition-colors cursor-pointer"
    onClick={() => onClick && onClick(property)}
    data-testid={`property-row-${property.id}`}
  >
    <div className="flex items-center gap-4">
      <img 
        src={property.images?.[0] || "https://via.placeholder.com/60"} 
        alt={property.title}
        className="w-16 h-12 object-cover rounded-lg"
      />
      <div>
        <p className="font-medium text-slate-800">{property.title}</p>
        <p className="text-sm text-slate-500 flex items-center gap-1">
          <MapPin className="w-3 h-3" /> {property.municipality}
        </p>
        {/* Show features summary */}
        {property.features && (
          <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
            {property.features.bedrooms !== undefined && (
              <span className="flex items-center gap-1">
                <Bed className="w-3 h-3" /> {property.features.bedrooms}
              </span>
            )}
            {property.features.bathrooms !== undefined && (
              <span className="flex items-center gap-1">
                <Bath className="w-3 h-3" /> {property.features.bathrooms}
              </span>
            )}
            {(property.features.land_area_m2 || property.features.area) && (
              <span className="flex items-center gap-1" title="Terreno">
                <Square className="w-3 h-3" /> {property.features.land_area_m2 || property.features.area}m²
              </span>
            )}
            {property.features.construction_area_m2 && (
              <span className="flex items-center gap-1" title="Construido">
                <Home className="w-3 h-3" /> {property.features.construction_area_m2}m²
              </span>
            )}
            {property.features.price !== undefined && (
              <span className="font-medium text-primary">
                ${(property.features.price / 1000000).toFixed(1)}M
              </span>
            )}
          </div>
        )}
      </div>
    </div>
    <div className="flex items-center gap-4">
      <div className="flex gap-1">
        {property.vibe_tags?.slice(0, 3).map(tagId => {
          const tag = getAllVibeTagsArray().find(t => t.id === tagId);
          return tag ? <VibeTag key={tagId} tag={tag} size="sm" /> : null;
        })}
      </div>
      <Badge variant="outline">{property.match_count || 0} matches</Badge>
      <div className="text-right">
        <p className="font-semibold text-primary">{property.avg_match_score || 0}%</p>
        <p className="text-xs text-slate-500">avg match</p>
      </div>
      <ChevronRight className="w-5 h-5 text-slate-300" />
    </div>
  </div>
);

// Match Feed Item
const MatchFeedItem = ({ match }) => (
  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
      <Heart className="w-5 h-5 text-primary" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-slate-800 truncate">
        {match.shopper_name} → {match.property_title}
      </p>
      <p className="text-xs text-slate-500">{match.municipality}</p>
    </div>
    <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-0">
      {match.match_score}%
    </Badge>
  </div>
);

// Pipeline Phase Card
const PipelinePhase = ({ title, count, stale, icon: Icon, color, items }) => (
  <Card className="flex-1 min-w-[280px]">
    <CardHeader className="pb-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-lg ${color}`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{count}</Badge>
          {stale > 0 && (
            <Badge variant="destructive" className="gap-1">
              <Clock className="w-3 h-3" /> {stale}
            </Badge>
          )}
        </div>
      </div>
    </CardHeader>
    <CardContent className="pt-0">
      <ScrollArea className="h-[300px]">
        <div className="space-y-2">
          {items?.slice(0, 10).map((item, idx) => (
            <div key={idx} className="p-3 bg-slate-50 rounded-lg text-sm">
              <p className="font-medium text-slate-700 truncate">
                {item.property?.title || item.contract_data?.property_title || "Operación"}
              </p>
              <p className="text-xs text-slate-500">
                {item.shopper?.name || "Usuario"} • {item.match_score || item.status}
              </p>
            </div>
          ))}
          {(!items || items.length === 0) && (
            <p className="text-sm text-slate-400 text-center py-4">Sin operaciones</p>
          )}
        </div>
      </ScrollArea>
    </CardContent>
  </Card>
);

// Role Distribution Chart (Simple)
const RoleDistribution = ({ data }) => {
  const total = (data?.shoppers || 0) + (data?.owners || 0) + (data?.agents || 0);
  if (total === 0) return null;
  
  const roles = [
    { name: "Compradores", count: data?.shoppers || 0, color: "bg-blue-500" },
    { name: "Propietarios", count: data?.owners || 0, color: "bg-green-500" },
    { name: "Agentes", count: data?.agents || 0, color: "bg-purple-500" }
  ];
  
  return (
    <div className="space-y-3">
      {roles.map(role => (
        <div key={role.name} className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">{role.name}</span>
            <span className="font-medium">{role.count} ({Math.round(role.count / total * 100)}%)</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className={`h-full ${role.color} rounded-full transition-all`}
              style={{ width: `${(role.count / total) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

// Match Distribution Histogram
const MatchHistogram = ({ data }) => {
  if (!data) return null;
  const maxValue = Math.max(data["70-80"] || 0, data["80-90"] || 0, data["90-100"] || 0, 1);
  
  const ranges = [
    { label: "70-80%", value: data["70-80"] || 0, color: "bg-yellow-500" },
    { label: "80-90%", value: data["80-90"] || 0, color: "bg-orange-500" },
    { label: "90-100%", value: data["90-100"] || 0, color: "bg-green-500" }
  ];
  
  return (
    <div className="flex items-end gap-4 h-32">
      {ranges.map(range => (
        <div key={range.label} className="flex-1 flex flex-col items-center">
          <div 
            className={`w-full ${range.color} rounded-t-lg transition-all`}
            style={{ height: `${Math.max((range.value / maxValue) * 100, 5)}%` }}
          />
          <p className="text-xs text-slate-500 mt-2">{range.label}</p>
          <p className="text-sm font-bold">{range.value}</p>
        </div>
      ))}
    </div>
  );
};

// Main Admin Dashboard Component
export const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState({ users: [], total: 0 });
  const [properties, setProperties] = useState({ properties: [], total: 0 });
  const [pendingProperties, setPendingProperties] = useState({ pending_properties: [], count: 0 });
  const [matchFeed, setMatchFeed] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [geoStats, setGeoStats] = useState([]);
  const [vibeAnalysis, setVibeAnalysis] = useState(null);
  const [alerts, setAlerts] = useState({ alerts: [], critical_count: 0, warning_count: 0 });
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [municipalityFilter, setMunicipalityFilter] = useState("");
  const [vibeTagFilter, setVibeTagFilter] = useState("");

  // Debounce searchQuery so we don't hit the API on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Modal states
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", phone_whatsapp: "", search_city: "", role: "" });
  const [adminForm, setAdminForm] = useState({ name: "", email: "", password: "" });
  const [actionLoading, setActionLoading] = useState(false);

  // New states for pending users and subscriptions
  const [pendingUsers, setPendingUsers] = useState({ pending_agents: [], pending_owners: [], total_pending: 0 });
  const [pendingPayments, setPendingPayments] = useState({ agent_payments: [], subscription_payments: [], total_pending: 0 });
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [showPropertyDetailsModal, setShowPropertyDetailsModal] = useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [selectedPropertyDetails, setSelectedPropertyDetails] = useState(null);
  const [subscriptionForm, setSubscriptionForm] = useState({ userId: "", plan: "monthly", paymentMethod: "transfer" });
  
  // Master Partner modal states
  const [showMasterPartnerModal, setShowMasterPartnerModal] = useState(false);
  const [masterPartnerForm, setMasterPartnerForm] = useState({ userId: "", region: "" });
  const [showAssignAgentModal, setShowAssignAgentModal] = useState(false);
  const [assignAgentForm, setAssignAgentForm] = useState({ agentId: "", masterPartnerId: "" });
  const [masterPartnersList, setMasterPartnersList] = useState([]);

  // Password reset modal states
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [resetTargetUser, setResetTargetUser] = useState(null);
  const [resetting, setResetting] = useState(false);

  // Backend connectivity indicator (surfaces silent 5xx / network errors)
  const [backendError, setBackendError] = useState(null);
  
  // Master Partner regions available
  const MASTER_PARTNER_REGIONS = [
    { id: "cdmx", name: "CDMX y Zona Metropolitana", region: "Ciudad de México" },
    { id: "monterrey", name: "Monterrey y Área Metropolitana", region: "Nuevo León" },
    { id: "guadalajara", name: "Guadalajara y Zona Metropolitana", region: "Jalisco" },
    { id: "bajio", name: "Región del Bajío", region: "Guanajuato, Querétaro, Aguascalientes" },
    { id: "riviera_maya", name: "Riviera Maya", region: "Quintana Roo" },
    { id: "los_cabos", name: "Los Cabos", region: "Baja California Sur" },
    { id: "puebla", name: "Puebla y Tlaxcala", region: "Puebla, Tlaxcala" },
    { id: "sureste", name: "Sureste", region: "Yucatán, Campeche, Tabasco" }
  ];

  const fetchAllData = useCallback(async () => {
    try {
      const [analyticsRes, usersRes, propertiesRes, pendingPropsRes, matchFeedRes, pipelineRes, geoRes, vibeRes, alertsRes, pendingUsersRes, pendingPaymentsRes, plansRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`),
        axios.get(`${API}/admin/users`, { params: { search: debouncedSearch, role: roleFilter, limit: 50 } }),
        axios.get(`${API}/admin/properties`, { params: { search: debouncedSearch, municipality: municipalityFilter, vibe_tag: vibeTagFilter, limit: 50 } }),
        axios.get(`${API}/admin/pending-properties`),
        axios.get(`${API}/admin/matches/feed`),
        axios.get(`${API}/admin/pipeline`),
        axios.get(`${API}/admin/geo-stats`),
        axios.get(`${API}/admin/vibe-analysis`, { params: { municipality: municipalityFilter || undefined } }),
        axios.get(`${API}/admin/alerts`),
        axios.get(`${API}/admin/pending-users`).catch(() => ({ data: { pending_agents: [], pending_owners: [], total_pending: 0 } })),
        axios.get(`${API}/admin/pending-payments`).catch(() => ({ data: { agent_payments: [], subscription_payments: [], total_pending: 0 } })),
        axios.get(`${API}/subscription-plans`).catch(() => ({ data: { plans: [] } }))
      ]);
      
      setAnalytics(analyticsRes.data);
      setUsers(usersRes.data);
      setProperties(propertiesRes.data);
      setPendingProperties(pendingPropsRes.data);
      setMatchFeed(matchFeedRes.data);
      setPipeline(pipelineRes.data);
      setGeoStats(geoRes.data);
      setVibeAnalysis(vibeRes.data);
      setAlerts(alertsRes.data);
      setPendingUsers(pendingUsersRes.data);
      setPendingPayments(pendingPaymentsRes.data);
      setSubscriptionPlans(plansRes.data.plans || []);
      setLastUpdate(new Date());
      setBackendError(null);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      const status = error?.response?.status;
      const isNetworkOrGateway = !status || status === 502 || status === 503 || status === 504 || status === 520 || status === 521 || status === 522;
      if (isNetworkOrGateway) {
        setBackendError({
          type: "network",
          status,
          message:
            "No se pudo conectar con el backend. Los indicadores en 0 NO son datos reales — la API está temporalmente inaccesible.",
        });
      } else if (status === 401 || status === 403) {
        setBackendError({
          type: "auth",
          status,
          message: "Tu sesión expiró o no tienes permisos. Cierra sesión y vuelve a entrar.",
        });
      } else {
        setBackendError({
          type: "server",
          status,
          message: `Error ${status || "desconocido"} del servidor al cargar el dashboard. Intenta refrescar.`,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, roleFilter, municipalityFilter, vibeTagFilter]);

  useEffect(() => {
    fetchAllData();
    // Polling every 30 seconds
    const interval = setInterval(fetchAllData, 30000);
    return () => clearInterval(interval);
  }, [fetchAllData]);

  // Fetch Master Partners list
  const fetchMasterPartners = async () => {
    try {
      const response = await axios.get(`${API}/admin/master-partners-users`);
      setMasterPartnersList(response.data.master_partners || []);
    } catch (error) {
      console.error("Error fetching master partners:", error);
    }
  };

  useEffect(() => {
    fetchMasterPartners();
  }, []);

  const handleSeedDemo = async () => {
    try {
      const response = await axios.post(`${API}/admin/seed-demo-data`);
      toast.success(`Datos demo creados: ${response.data.created.properties} propiedades, ${response.data.created.users} usuarios`);
      fetchAllData();
    } catch (error) {
      toast.error("Error al crear datos demo");
    }
  };

  const handleExport = async (dataType) => {
    try {
      const response = await axios.get(`${API}/admin/export`, { params: { data_type: dataType } });
      const blob = new Blob([JSON.stringify(response.data.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `findperty_${dataType}_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      toast.success(`${response.data.count} registros exportados`);
    } catch (error) {
      toast.error("Error al exportar datos");
    }
  };

  // User Management Functions
  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name || "",
      email: user.email || "",
      phone_whatsapp: user.phone_whatsapp || "",
      search_city: user.search_city || "",
      role: user.role || ""
    });
    setShowEditUser(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteUser = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      await axios.delete(`${API}/admin/users/${selectedUser.id}`);
      toast.success(`Usuario ${selectedUser.name} eliminado exitosamente`);
      setShowDeleteConfirm(false);
      setSelectedUser(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar usuario");
    } finally {
      setActionLoading(false);
    }
  };

  const saveUserEdits = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}`, editForm);
      toast.success("Usuario actualizado exitosamente");
      setShowEditUser(false);
      setSelectedUser(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar usuario");
    } finally {
      setActionLoading(false);
    }
  };

  const createNewAdmin = async () => {
    if (!adminForm.name || !adminForm.email || !adminForm.password) {
      toast.error("Todos los campos son requeridos");
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/create-admin`, adminForm);
      toast.success(`Administrador ${adminForm.name} creado exitosamente`);
      setShowCreateAdmin(false);
      setAdminForm({ name: "", email: "", password: "" });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear administrador");
    } finally {
      setActionLoading(false);
    }
  };

  // Property Approval Functions
  const approveProperty = async (propertyId) => {
    try {
      await axios.post(`${API}/admin/properties/${propertyId}/approve`);
      toast.success("Propiedad aprobada exitosamente");
      // Optimistic UI: remove approved property immediately from local pending list
      setPendingProperties((prev) => ({
        pending_properties: (prev?.pending_properties || []).filter((p) => p.id !== propertyId),
        count: Math.max(0, (prev?.count || 1) - 1),
      }));
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aprobar propiedad");
    }
  };

  const rejectProperty = async (propertyId) => {
    try {
      await axios.post(`${API}/admin/properties/${propertyId}/reject`);
      toast.success("Propiedad rechazada");
      // Optimistic UI: remove rejected property immediately from local pending list
      setPendingProperties((prev) => ({
        pending_properties: (prev?.pending_properties || []).filter((p) => p.id !== propertyId),
        count: Math.max(0, (prev?.count || 1) - 1),
      }));
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al rechazar propiedad");
    }
  };

  const deleteProperty = async (propertyId) => {
    try {
      await axios.delete(`${API}/admin/properties/${propertyId}`);
      toast.success("Propiedad eliminada");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar propiedad");
    }
  };

  // User Approval Functions
  const approveUser = async (userId) => {
    try {
      setActionLoading(true);
      await axios.post(`${API}/admin/users/${userId}/approve`);
      toast.success("Usuario aprobado exitosamente");
      // Optimistic UI: remove approved user from pending lists
      setPendingUsers((prev) => ({
        pending_agents: (prev?.pending_agents || []).filter((u) => u.id !== userId),
        pending_owners: (prev?.pending_owners || []).filter((u) => u.id !== userId),
        total_pending: Math.max(0, (prev?.total_pending || 1) - 1),
      }));
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aprobar usuario");
    } finally {
      setActionLoading(false);
    }
  };

  const rejectUser = async (userId, reason = "No cumple con los requisitos") => {
    try {
      setActionLoading(true);
      await axios.post(`${API}/admin/users/${userId}/reject`, null, { params: { reason } });
      toast.success("Usuario rechazado");
      // Optimistic UI: remove rejected user from pending lists
      setPendingUsers((prev) => ({
        pending_agents: (prev?.pending_agents || []).filter((u) => u.id !== userId),
        pending_owners: (prev?.pending_owners || []).filter((u) => u.id !== userId),
        total_pending: Math.max(0, (prev?.total_pending || 1) - 1),
      }));
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al rechazar usuario");
    } finally {
      setActionLoading(false);
    }
  };

  // Subscription Activation
  const activateSubscription = async () => {
    try {
      setActionLoading(true);
      await axios.post(`${API}/admin/users/${subscriptionForm.userId}/activate-subscription`, null, {
        params: {
          plan: subscriptionForm.plan,
          payment_method: subscriptionForm.paymentMethod
        }
      });
      toast.success("Suscripción activada exitosamente");
      setShowSubscriptionModal(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al activar suscripción");
    } finally {
      setActionLoading(false);
    }
  };

  // Set Master Partner
  const setMasterPartner = async () => {
    try {
      setActionLoading(true);
      await axios.post(`${API}/admin/users/${masterPartnerForm.userId}/set-master-partner`, null, {
        params: { master_partner_id: masterPartnerForm.region }
      });
      toast.success("Agente configurado como Master Partner");
      setShowMasterPartnerModal(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al configurar Master Partner");
    } finally {
      setActionLoading(false);
    }
  };

  // Remove Master Partner status
  const removeMasterPartner = async (userId) => {
    try {
      setActionLoading(true);
      await axios.put(`${API}/admin/users/${userId}`, {
        is_master_partner: false,
        master_partner_region: null
      });
      toast.success("Estatus de Master Partner removido");
      fetchAllData();
      fetchMasterPartners();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al remover Master Partner");
    } finally {
      setActionLoading(false);
    }
  };

  // Assign Agent to Master Partner
  const assignAgentToMasterPartner = async () => {
    try {
      setActionLoading(true);
      await axios.post(`${API}/admin/users/${assignAgentForm.agentId}/assign-to-master-partner`, null, {
        params: { master_partner_user_id: assignAgentForm.masterPartnerId }
      });
      toast.success("Agente asignado al Master Partner exitosamente");
      setShowAssignAgentModal(false);
      fetchAllData();
      // Refresh user details if viewing
      if (selectedUserDetails && selectedUserDetails.user?.id === assignAgentForm.agentId) {
        viewUserDetails(assignAgentForm.agentId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al asignar agente");
    } finally {
      setActionLoading(false);
    }
  };

  // Approve Payment
  const approvePayment = async (paymentId) => {
    try {
      setActionLoading(true);
      await axios.post(`${API}/admin/agents/payments/${paymentId}/approve`);
      toast.success("Pago aprobado");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aprobar pago");
    } finally {
      setActionLoading(false);
    }
  };

  // View User Details
  const viewUserDetails = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/user-details/${userId}`);
      setSelectedUserDetails(response.data);
      setShowUserDetailsModal(true);
    } catch (error) {
      toast.error("Error al cargar detalles del usuario");
    }
  };

  // Reset user password (admin action)
  const [customResetPassword, setCustomResetPassword] = useState("");

  const openResetPassword = (user) => {
    setResetTargetUser(user);
    setResetPasswordResult(null);
    setCustomResetPassword("");
    setShowResetPasswordModal(true);
  };

  const confirmResetPassword = async () => {
    if (!resetTargetUser) return;
    try {
      setResetting(true);
      const body = customResetPassword.trim()
        ? { temp_password: customResetPassword.trim() }
        : {};
      const response = await axios.put(
        `${API}/admin/users/${resetTargetUser.id}/reset-password`,
        body
      );
      setResetPasswordResult(response.data);
      if (response.data?.email_status === "sent") {
        toast.success("Contraseña restablecida. Email enviado al usuario.");
      } else {
        toast.success("Contraseña restablecida. Comparte la temporal con el usuario.");
      }
    } catch (error) {
      const msg = error.response?.data?.detail || "Error al restablecer contraseña";
      toast.error(msg);
    } finally {
      setResetting(false);
    }
  };

  const copyTempPassword = async () => {
    if (!resetPasswordResult?.temp_password) return;
    try {
      await navigator.clipboard.writeText(resetPasswordResult.temp_password);
      toast.success("Contraseña copiada al portapapeles");
    } catch (e) {
      toast.error("No se pudo copiar al portapapeles");
    }
  };

  // View Property Details
  const viewPropertyDetails = async (propertyId) => {
    try {
      const response = await axios.get(`${API}/admin/property-details/${propertyId}`);
      setSelectedPropertyDetails(response.data);
      setShowPropertyDetailsModal(true);
    } catch (error) {
      toast.error("Error al cargar detalles de la propiedad");
    }
  };

  // Handle property row click - loads full details
  const handlePropertyClick = async (property) => {
    viewPropertyDetails(property.id);
  };

  // Open subscription modal for a user
  const openSubscriptionModal = (userId) => {
    setSubscriptionForm({ ...subscriptionForm, userId });
    setShowSubscriptionModal(true);
  };

  // Calculate total pending count
  const totalPendingCount = pendingProperties.count + pendingUsers.total_pending + pendingPayments.total_pending;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-10 h-10 text-primary animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-white p-6 flex flex-col">
        <div className="mb-8">
          <h1 className="text-xl font-bold text-primary">Findperty</h1>
          <p className="text-sm text-slate-400">Lote Uno - Control Center</p>
        </div>
        
        <nav className="space-y-2 flex-1">
          {[
            { id: "overview", label: "Vista General", icon: BarChart3 },
            { id: "users", label: "Usuarios", icon: Users },
            { id: "pending", label: "Pendientes", icon: Clock, badge: totalPendingCount },
            { id: "properties", label: "Propiedades", icon: Building },
            { id: "matches", label: "Match Engine", icon: Heart },
            { id: "pipeline", label: "Pipeline CRM", icon: Activity },
            { id: "geo", label: "Geo-Intelligence", icon: MapPin },
            { id: "analytics", label: "Analytics Pro", icon: PieChart },
            { id: "valuation", label: "Valuación MVIP", icon: Calculator },
            { id: "terreno", label: "Potencial Terreno", icon: Map },
            { id: "colonias", label: "Colonias MVIP", icon: MapPin },
            { id: "alerts", label: "Alertas", icon: Bell, badge: alerts.critical_count }
          ].map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === item.id ? "bg-primary text-slate-900 font-medium" : "text-slate-300 hover:bg-slate-800"
              }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
              {item.badge > 0 && (
                <Badge variant="destructive" className="ml-auto">{item.badge}</Badge>
              )}
            </button>
          ))}
        </nav>
        
        <div className="pt-4 border-t border-slate-700 space-y-2">
          <Button variant="outline" size="sm" className="w-full justify-start text-slate-300 border-slate-700 hover:bg-slate-800" onClick={handleSeedDemo}>
            <Zap className="w-4 h-4 mr-2" /> Crear Demo Data
          </Button>
          <Button variant="outline" size="sm" className="w-full justify-start text-slate-300 border-slate-700 hover:bg-slate-800" onClick={fetchAllData}>
            <RefreshCw className="w-4 h-4 mr-2" /> Actualizar
          </Button>
        </div>
        
        <div className="pt-4 text-xs text-slate-500">
          Última actualización: {lastUpdate.toLocaleTimeString()}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-slate-800">
              {activeTab === "overview" && "Vista General"}
              {activeTab === "users" && "Gestión de Usuarios"}
              {activeTab === "pending" && "Aprobaciones Pendientes"}
              {activeTab === "properties" && "Inventario de Propiedades"}
              {activeTab === "matches" && "Motor de Matches"}
              {activeTab === "pipeline" && "Pipeline de Operaciones"}
              {activeTab === "geo" && "Geo-Intelligence"}
              {activeTab === "analytics" && "Analytics Avanzados"}
              {activeTab === "valuation" && "Valuación Inmobiliaria (MVIP)"}
              {activeTab === "colonias" && "Colonias & Factores MVIP"}
              {activeTab === "alerts" && "Centro de Alertas"}
            </h2>
            <p className="text-slate-500">Panel de control Lote Uno</p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Global Filters */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input 
                placeholder="Buscar..." 
                className="pl-9 w-64 bg-white"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <Select value={municipalityFilter || "_all"} onValueChange={(v) => setMunicipalityFilter(v === "_all" ? "" : v)}>
              <SelectTrigger className="w-40 bg-white">
                <SelectValue placeholder="Municipio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Todos</SelectItem>
                {geoStats.filter(g => g.density > 0).map(g => (
                  <SelectItem key={g.city} value={g.city}>{g.city}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button variant="outline" onClick={() => handleExport("properties")}>
              <Download className="w-4 h-4 mr-2" /> Exportar
            </Button>
          </div>
        </div>

        {/* Backend connectivity banner — surfaces silent 5xx/network errors */}
        {backendError && (
          <div
            className={`mb-6 rounded-xl border p-4 flex items-start gap-3 ${
              backendError.type === "network"
                ? "bg-red-50 border-red-300 text-red-900"
                : backendError.type === "auth"
                ? "bg-amber-50 border-amber-300 text-amber-900"
                : "bg-orange-50 border-orange-300 text-orange-900"
            }`}
            data-testid="backend-error-banner"
          >
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-bold">
                {backendError.type === "network"
                  ? "⚠️ Backend inaccesible"
                  : backendError.type === "auth"
                  ? "Sesión inválida"
                  : "Error del servidor"}
                {backendError.status ? ` · HTTP ${backendError.status}` : ""}
              </p>
              <p className="text-sm mt-1">{backendError.message}</p>
              {backendError.type === "network" && (
                <p className="text-xs mt-2 opacity-80">
                  Producción parece estar en un ciclo de reinicio. Reporta a
                  Emergent Support: pod crash-loop en{" "}
                  <code className="bg-white/50 px-1 rounded">real-estate-hub-116.emergent.host</code>.
                </p>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => fetchAllData()}
              className="shrink-0"
              data-testid="backend-error-retry"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reintentar
            </Button>
          </div>
        )}
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* KPI Tiles */}
            <div className="grid grid-cols-4 gap-6">
              <KPITile 
                title="Total Usuarios" 
                value={analytics?.total_users || 0}
                subtitle={`${analytics?.users_by_role?.shoppers || 0} compradores, ${analytics?.users_by_role?.owners || 0} propietarios`}
                icon={Users}
                color="blue"
              />
              <KPITile 
                title="Propiedades Activas" 
                value={analytics?.active_properties || 0}
                subtitle={`${analytics?.freemium_properties || 0} freemium, ${analytics?.premium_properties || 0} premium`}
                icon={Building}
                color="green"
              />
              <KPITile 
                title="Match Rate Promedio" 
                value={`${analytics?.avg_match_rate || 0}%`}
                subtitle={`${analytics?.total_matches || 0} matches totales`}
                icon={Heart}
                color="primary"
              />
              <KPITile 
                title="Tasa de Conversión" 
                value={`${analytics?.conversion_rate || 0}%`}
                subtitle={`${analytics?.signed_contracts || 0} contratos firmados`}
                icon={FileText}
                color="orange"
              />
            </div>
            
            {/* Secondary Stats */}
            <div className="grid grid-cols-3 gap-6">
              {/* Role Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Distribución de Roles</CardTitle>
                </CardHeader>
                <CardContent>
                  <RoleDistribution data={analytics?.users_by_role} />
                </CardContent>
              </Card>
              
              {/* Match Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Distribución de Matches</CardTitle>
                  <CardDescription>Por rango de compatibilidad</CardDescription>
                </CardHeader>
                <CardContent>
                  <MatchHistogram data={analytics?.match_distribution} />
                </CardContent>
              </Card>
              
              {/* Recent Match Feed */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Matches Recientes</CardTitle>
                  <CardDescription>Actividad en tiempo real</CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[180px]">
                    <div className="space-y-2">
                      {matchFeed.slice(0, 5).map((match, idx) => (
                        <MatchFeedItem key={idx} match={match} />
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
            
            {/* Vibe Analysis */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="w-5 h-5 text-primary" />
                  Análisis de Vibes: Oferta vs Demanda
                </CardTitle>
                <CardDescription>Qué buscan los compradores vs qué ofrecen los propietarios</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-medium text-slate-700 mb-3">Top Demandados (Compradores)</h4>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(vibeAnalysis?.shopper_preferences || {}).slice(0, 10).map(([tagId, count]) => {
                        const tag = getAllVibeTagsArray().find(t => t.id === tagId);
                        return tag ? (
                          <div key={tagId} className="flex items-center gap-1">
                            <VibeTag tag={tag} size="sm" />
                            <span className="text-xs text-slate-500">({count})</span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-700 mb-3">Top Ofertados (Propietarios)</h4>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(vibeAnalysis?.owner_listings || {}).slice(0, 10).map(([tagId, count]) => {
                        const tag = getAllVibeTagsArray().find(t => t.id === tagId);
                        return tag ? (
                          <div key={tagId} className="flex items-center gap-1">
                            <VibeTag tag={tag} size="sm" />
                            <span className="text-xs text-slate-500">({count})</span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="space-y-6">
            <div className="flex gap-3 justify-between">
              <div className="flex gap-3">
                <Select value={roleFilter || "_all"} onValueChange={(v) => setRoleFilter(v === "_all" ? "" : v)}>
                  <SelectTrigger className="w-40 bg-white">
                    <SelectValue placeholder="Filtrar por rol" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Todos los roles</SelectItem>
                    <SelectItem value="shopper">Comprador</SelectItem>
                    <SelectItem value="owner">Propietario</SelectItem>
                    <SelectItem value="agent">Agente</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => handleExport("users")}>
                  <Download className="w-4 h-4 mr-2" /> Exportar Usuarios
                </Button>
              </div>
              <Button onClick={() => setShowCreateAdmin(true)} className="bg-primary text-[#384451]" data-testid="create-admin-btn">
                <UserPlus className="w-4 h-4 mr-2" /> Crear Administrador
              </Button>
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle>Lista de Usuarios ({users.total})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {users.users.map(user => (
                    <UserRow 
                      key={user.id} 
                      user={user} 
                      onView={(u) => viewUserDetails(u.id)} 
                      onEdit={handleEditUser}
                      onDelete={handleDeleteUser}
                      onSetMasterPartner={(u) => {
                        setMasterPartnerForm({ userId: u.id, region: "" });
                        setShowMasterPartnerModal(true);
                      }}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Pending Approvals Tab - Enhanced */}
        {activeTab === "pending" && (
          <div className="space-y-6">
            {/* Sub-tabs for different pending items */}
            <Tabs defaultValue="properties" className="w-full">
              <TabsList className="grid grid-cols-3 w-full max-w-lg">
                <TabsTrigger value="properties" className="flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  Propiedades
                  {pendingProperties.count > 0 && <Badge variant="destructive" className="ml-1">{pendingProperties.count}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="users" className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Usuarios
                  {pendingUsers.total_pending > 0 && <Badge variant="destructive" className="ml-1">{pendingUsers.total_pending}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="payments" className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Pagos
                  {pendingPayments.total_pending > 0 && <Badge variant="destructive" className="ml-1">{pendingPayments.total_pending}</Badge>}
                </TabsTrigger>
              </TabsList>

              {/* Pending Properties */}
              <TabsContent value="properties" className="mt-6">
                {pendingProperties.count === 0 ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-slate-700 mb-2">¡Todo al día!</h3>
                      <p className="text-slate-500">No hay propiedades pendientes de aprobación</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {pendingProperties.pending_properties.map(prop => (
                      <Card key={prop.id} className="overflow-hidden border-l-4 border-l-orange-500" data-testid={`pending-prop-${prop.id}`}>
                        <div className="flex flex-col sm:flex-row">
                          <img 
                            src={prop.images?.[0] || "https://via.placeholder.com/160"} 
                            alt={prop.title} 
                            className="w-full sm:w-40 h-40 object-cover"
                          />
                          <div className="flex-1 p-4">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <h3 className="font-semibold text-lg">{prop.title}</h3>
                                <p className="text-sm text-slate-500"><MapPin className="w-3 h-3 inline mr-1" />{prop.municipality}</p>
                              </div>
                              <Badge variant="secondary">
                                <Clock className="w-3 h-3 mr-1" /> Pendiente
                              </Badge>
                            </div>
                            
                            {prop.agent_info && (
                              <p className="text-sm text-slate-600 mb-1">
                                <span className="font-medium">Agente:</span> {prop.agent_info.name} ({prop.agent_info.email})
                              </p>
                            )}
                            {prop.owner_info && (
                              <p className="text-sm text-slate-600 mb-2">
                                <span className="font-medium">Propietario:</span> {prop.owner_info.name}
                              </p>
                            )}
                            
                            <div className="flex gap-1 mb-3 flex-wrap">
                              {prop.vibe_tags?.slice(0, 3).map(tagId => {
                                const tag = getAllVibeTagsArray().find(t => t.id === tagId);
                                return tag ? <VibeTag key={tagId} tag={tag} size="sm" /> : null;
                              })}
                            </div>
                            
                            <div className="flex gap-2 flex-wrap">
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="text-blue-600"
                                onClick={() => viewPropertyDetails(prop.id)}
                              >
                                <Eye className="w-4 h-4 mr-1" /> Ver
                              </Button>
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => approveProperty(prop.id)}
                                data-testid={`approve-${prop.id}`}
                              >
                                <Check className="w-4 h-4 mr-1" /> Aprobar
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => rejectProperty(prop.id)}
                                data-testid={`reject-${prop.id}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" /> Rechazar
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Pending Users */}
              <TabsContent value="users" className="mt-6">
                {pendingUsers.total_pending === 0 ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-slate-700 mb-2">¡Sin usuarios pendientes!</h3>
                      <p className="text-slate-500">Todos los usuarios han sido procesados</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-6">
                    {/* Pending Agents */}
                    {pendingUsers.pending_agents.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Shield className="w-5 h-5 text-purple-600" />
                            Agentes Pendientes ({pendingUsers.pending_agents.length})
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            {pendingUsers.pending_agents.map(agent => (
                              <div key={agent.user_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                                <div className="flex items-center gap-4">
                                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                                    <Shield className="w-6 h-6 text-purple-600" />
                                  </div>
                                  <div>
                                    <p className="font-semibold">{agent.user?.name || "N/A"}</p>
                                    <p className="text-sm text-slate-500">{agent.user?.email}</p>
                                    <div className="flex gap-2 mt-1">
                                      {agent.agency_name && <Badge variant="outline">{agent.agency_name}</Badge>}
                                      {agent.municipality && <Badge variant="secondary"><MapPin className="w-3 h-3 mr-1" />{agent.municipality}</Badge>}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <Button size="sm" variant="ghost" onClick={() => viewUserDetails(agent.user_id)}>
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => approveUser(agent.user_id)} disabled={actionLoading}>
                                    <Check className="w-4 h-4 mr-1" /> Aprobar
                                  </Button>
                                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => rejectUser(agent.user_id)} disabled={actionLoading}>
                                    <XCircle className="w-4 h-4 mr-1" /> Rechazar
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Pending Owners */}
                    {pendingUsers.pending_owners.length > 0 && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2">
                            <Home className="w-5 h-5 text-green-600" />
                            Propietarios Pendientes ({pendingUsers.pending_owners.length})
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            {pendingUsers.pending_owners.map(owner => (
                              <div key={owner.user_id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                                <div className="flex items-center gap-4">
                                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                                    <Home className="w-6 h-6 text-green-600" />
                                  </div>
                                  <div>
                                    <p className="font-semibold">{owner.user?.name || "N/A"}</p>
                                    <p className="text-sm text-slate-500">{owner.user?.email}</p>
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <Button size="sm" variant="ghost" onClick={() => viewUserDetails(owner.user_id)}>
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => approveUser(owner.user_id)} disabled={actionLoading}>
                                    <Check className="w-4 h-4 mr-1" /> Aprobar
                                  </Button>
                                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => rejectUser(owner.user_id)} disabled={actionLoading}>
                                    <XCircle className="w-4 h-4 mr-1" /> Rechazar
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}
              </TabsContent>

              {/* Pending Payments */}
              <TabsContent value="payments" className="mt-6">
                {pendingPayments.total_pending === 0 ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-slate-700 mb-2">¡Sin pagos pendientes!</h3>
                      <p className="text-slate-500">Todos los pagos han sido procesados</p>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        Pagos por Transferencia Pendientes
                      </CardTitle>
                      <CardDescription>Pagos realizados por transferencia bancaria esperando verificación</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {pendingPayments.agent_payments.map(payment => (
                          <div key={payment.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                                <FileText className="w-6 h-6 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-semibold">{payment.user?.name || "Usuario"}</p>
                                <p className="text-sm text-slate-500">{payment.user?.email}</p>
                                <div className="flex gap-2 mt-1">
                                  <Badge variant="outline" className="capitalize">{payment.plan}</Badge>
                                  <Badge variant="secondary">${payment.amount?.toLocaleString()} MXN</Badge>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => approvePayment(payment.id)} disabled={actionLoading}>
                                <Check className="w-4 h-4 mr-1" /> Aprobar Pago
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}

        {/* Valuation MVIP Tab (Admin) */}
        {activeTab === "valuation" && (
          <ValuationModule />
        )}

        {/* Terreno Tab (Admin) */}
        {activeTab === "terreno" && (
          <TerrenoModule />
        )}

        {/* Colonias MVIP Tab (Admin) */}
        {activeTab === "colonias" && (
          <ColoniasManager />
        )}

        {/* Properties Tab */}
        {activeTab === "properties" && (
          <div className="space-y-6">
            <div className="flex gap-3">
              <Select value={vibeTagFilter || "_all"} onValueChange={(v) => setVibeTagFilter(v === "_all" ? "" : v)}>
                <SelectTrigger className="w-48 bg-white">
                  <SelectValue placeholder="Filtrar por Vibe Tag" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todos los tags</SelectItem>
                  {getAllVibeTagsArray().map(tag => (
                    <SelectItem key={tag.id} value={tag.id}>{tag.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={() => handleExport("properties")}>
                <Download className="w-4 h-4 mr-2" /> Exportar Propiedades
              </Button>
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle>Inventario Global ({properties.total})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {properties.properties.map(property => (
                    <PropertyRow 
                      key={property.id} 
                      property={property} 
                      onClick={handlePropertyClick}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Matches Tab */}
        {activeTab === "matches" && (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Matches Totales</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold text-primary">{analytics?.total_matches || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Match Rate Promedio</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold text-green-600">{analytics?.avg_match_rate || 0}%</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Distribución</CardTitle>
                </CardHeader>
                <CardContent>
                  <MatchHistogram data={analytics?.match_distribution} />
                </CardContent>
              </Card>
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle>Feed de Matches en Tiempo Real</CardTitle>
                <CardDescription>Últimos 50 matches registrados</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {matchFeed.map((match, idx) => (
                      <MatchFeedItem key={idx} match={match} />
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Pipeline Tab */}
        {activeTab === "pipeline" && (
          <div className="space-y-6">
            {pipeline?.alerts?.total_stale > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <p className="text-red-700">
                  <strong>{pipeline.alerts.total_stale} operaciones</strong> llevan más de 48 horas estancadas y requieren atención.
                </p>
              </div>
            )}
            
            <div className="flex gap-4 overflow-x-auto pb-4">
              <PipelinePhase 
                title="Fase 1: Blind Match" 
                count={pipeline?.phases?.blind_match?.count || 0}
                stale={pipeline?.phases?.blind_match?.stale || 0}
                icon={Heart}
                color="bg-blue-500"
                items={pipeline?.phases?.blind_match?.items}
              />
              <ChevronRight className="w-6 h-6 text-slate-300 self-center flex-shrink-0" />
              <PipelinePhase 
                title="Fase 2: Intent/Contrato" 
                count={pipeline?.phases?.intent_contract?.count || 0}
                stale={pipeline?.phases?.intent_contract?.stale || 0}
                icon={FileText}
                color="bg-orange-500"
                items={pipeline?.phases?.intent_contract?.items}
              />
              <ChevronRight className="w-6 h-6 text-slate-300 self-center flex-shrink-0" />
              <PipelinePhase 
                title="Fase 3: Reveal" 
                count={pipeline?.phases?.reveal?.count || 0}
                stale={pipeline?.phases?.reveal?.stale || 0}
                icon={Eye}
                color="bg-green-500"
                items={pipeline?.phases?.reveal?.items}
              />
              <ChevronRight className="w-6 h-6 text-slate-300 self-center flex-shrink-0" />
              <PipelinePhase 
                title="Fase 4: Visita/Escrow" 
                count={pipeline?.phases?.visit_escrow?.count || 0}
                stale={pipeline?.phases?.visit_escrow?.stale || 0}
                icon={CheckCircle}
                color="bg-purple-500"
                items={pipeline?.phases?.visit_escrow?.items}
              />
            </div>
          </div>
        )}

        {/* Geo Tab */}
        {activeTab === "geo" && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <Card className="col-span-2">
                <CardHeader>
                  <CardTitle>Mapa de Densidad - México</CardTitle>
                  <CardDescription>Propiedades activas por región</CardDescription>
                </CardHeader>
                <CardContent>
                  <MexicoMap data={geoStats} />
                </CardContent>
              </Card>
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle>Top Ciudades por Actividad</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  {geoStats.filter(g => g.density > 0).slice(0, 10).map((city, idx) => (
                    <div key={city.city} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-bold text-slate-400">#{idx + 1}</span>
                        <div>
                          <p className="font-medium">{city.city}</p>
                          <p className="text-xs text-slate-500">{city.state}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-600">{city.supply} oferta</p>
                        <p className="text-sm text-blue-600">{city.demand} demanda</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Alerts Tab */}
        {activeTab === "alerts" && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <Card className="border-red-200 bg-red-50">
                <CardHeader>
                  <CardTitle className="text-red-700 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" /> Alertas Críticas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold text-red-600">{alerts.critical_count}</p>
                </CardContent>
              </Card>
              <Card className="border-yellow-200 bg-yellow-50">
                <CardHeader>
                  <CardTitle className="text-yellow-700 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" /> Advertencias
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold text-yellow-600">{alerts.warning_count}</p>
                </CardContent>
              </Card>
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle>Centro de Alertas</CardTitle>
                <CardDescription>Usuarios de alto riesgo y operaciones estancadas</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {alerts.alerts.map((alert, idx) => (
                      <div 
                        key={idx} 
                        className={`p-4 rounded-lg border ${
                          alert.severity === "critical" ? "bg-red-50 border-red-200" : "bg-yellow-50 border-yellow-200"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            {alert.severity === "critical" ? (
                              <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                            ) : (
                              <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
                            )}
                            <div>
                              <p className="font-medium text-slate-800">
                                {alert.type === "high_risk_user" ? `Usuario de Alto Riesgo: ${alert.user_name}` : `Operación Estancada: ${alert.phase}`}
                              </p>
                              {alert.factors && (
                                <ul className="mt-1 text-sm text-slate-600">
                                  {alert.factors.map((factor, i) => (
                                    <li key={i}>• {factor}</li>
                                  ))}
                                </ul>
                              )}
                              {alert.hours_stale && (
                                <p className="text-sm text-slate-500 mt-1">
                                  Estancado por {alert.hours_stale} horas
                                </p>
                              )}
                            </div>
                          </div>
                          {alert.risk_score && (
                            <Badge variant="destructive">Risk: {alert.risk_score}</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                    {alerts.alerts.length === 0 && (
                      <div className="text-center py-8 text-slate-500">
                        <Shield className="w-12 h-12 mx-auto mb-3 text-green-500" />
                        <p>No hay alertas activas. Todo está en orden.</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Advanced Analytics Tab */}
        {activeTab === "analytics" && (
          <AdvancedAnalytics />
        )}
      </main>

      {/* Create Admin Modal */}
      <Dialog open={showCreateAdmin} onOpenChange={setShowCreateAdmin}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Crear Nuevo Administrador</DialogTitle>
            <DialogDescription>
              Este usuario tendrá acceso completo al panel de control.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="admin-name">Nombre</Label>
              <Input
                id="admin-name"
                placeholder="Nombre completo"
                value={adminForm.name}
                onChange={(e) => setAdminForm({...adminForm, name: e.target.value})}
                data-testid="admin-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-email">Email</Label>
              <Input
                id="admin-email"
                type="email"
                placeholder="admin@ejemplo.com"
                value={adminForm.email}
                onChange={(e) => setAdminForm({...adminForm, email: e.target.value})}
                data-testid="admin-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password">Contraseña</Label>
              <Input
                id="admin-password"
                type="password"
                placeholder="••••••••"
                value={adminForm.password}
                onChange={(e) => setAdminForm({...adminForm, password: e.target.value})}
                data-testid="admin-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateAdmin(false)}>Cancelar</Button>
            <Button onClick={createNewAdmin} disabled={actionLoading} className="bg-primary text-[#384451]" data-testid="confirm-create-admin">
              {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Crear Administrador
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={showEditUser} onOpenChange={setShowEditUser}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Usuario</DialogTitle>
            <DialogDescription>
              Modifica los datos del usuario seleccionado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Nombre</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                data-testid="edit-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                data-testid="edit-email-input"
              />
            </div>
            {(selectedUser?.role === "shopper") && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="edit-phone">WhatsApp</Label>
                  <Input
                    id="edit-phone"
                    value={editForm.phone_whatsapp}
                    onChange={(e) => setEditForm({...editForm, phone_whatsapp: e.target.value})}
                    placeholder="+52 55 1234 5678"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-city">Ciudad de Búsqueda</Label>
                  <Input
                    id="edit-city"
                    value={editForm.search_city}
                    onChange={(e) => setEditForm({...editForm, search_city: e.target.value})}
                  />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label htmlFor="edit-role">Rol</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({...editForm, role: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar rol" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="shopper">Comprador</SelectItem>
                  <SelectItem value="owner">Propietario</SelectItem>
                  <SelectItem value="agent">Agente</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditUser(false)}>Cancelar</Button>
            <Button onClick={saveUserEdits} disabled={actionLoading} className="bg-primary text-[#384451]" data-testid="confirm-edit-user">
              {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Eliminar Usuario</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas eliminar a <strong>{selectedUser?.name}</strong>? 
              Esta acción no se puede deshacer y eliminará todos los datos asociados.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancelar</Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeleteUser} 
              disabled={actionLoading}
              data-testid="confirm-delete-user"
            >
              {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Eliminar Usuario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscription Activation Modal */}
      <Dialog open={showSubscriptionModal} onOpenChange={setShowSubscriptionModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Activar Suscripción</DialogTitle>
            <DialogDescription>Activa manualmente una suscripción para el usuario seleccionado</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Plan de Suscripción</Label>
              <Select value={subscriptionForm.plan} onValueChange={(v) => setSubscriptionForm({...subscriptionForm, plan: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un plan" />
                </SelectTrigger>
                <SelectContent>
                  {subscriptionPlans.filter(p => p.id !== "freemium").map(plan => (
                    <SelectItem key={plan.id} value={plan.id}>
                      {plan.name} - ${plan.price?.toLocaleString()} MXN ({plan.properties} propiedades)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Método de Pago Registrado</Label>
              <Select value={subscriptionForm.paymentMethod} onValueChange={(v) => setSubscriptionForm({...subscriptionForm, paymentMethod: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona método" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="transfer">Transferencia Bancaria</SelectItem>
                  <SelectItem value="stripe">Stripe/Tarjeta</SelectItem>
                  <SelectItem value="cash">Efectivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubscriptionModal(false)}>Cancelar</Button>
            <Button onClick={activateSubscription} disabled={actionLoading} className="bg-primary">
              {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Activar Suscripción
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Master Partner Modal */}
      <Dialog open={showMasterPartnerModal} onOpenChange={setShowMasterPartnerModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" /> Configurar Master Partner
            </DialogTitle>
            <DialogDescription>Asigna a este agente como Master Partner de una región</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Región</Label>
              <Select value={masterPartnerForm.region} onValueChange={(v) => setMasterPartnerForm({...masterPartnerForm, region: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona región" />
                </SelectTrigger>
                <SelectContent>
                  {MASTER_PARTNER_REGIONS.map(region => (
                    <SelectItem key={region.id} value={region.id}>
                      {region.name} - {region.region}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700">
                <strong>Nota:</strong> Como Master Partner, este agente tendrá acceso a funciones especiales 
                y podrá gestionar sub-agentes en su región asignada.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMasterPartnerModal(false)}>Cancelar</Button>
            <Button 
              onClick={setMasterPartner} 
              disabled={actionLoading || !masterPartnerForm.region} 
              className="bg-amber-500 hover:bg-amber-600"
            >
              {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Award className="w-4 h-4 mr-2" />}
              Asignar Master Partner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Agent to Master Partner Modal */}
      <Dialog open={showAssignAgentModal} onOpenChange={setShowAssignAgentModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-purple-500" /> Asignar a Master Partner
            </DialogTitle>
            <DialogDescription>Asigna este agente a trabajar bajo un Master Partner</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Master Partner</Label>
              <Select 
                value={assignAgentForm.masterPartnerId} 
                onValueChange={(v) => setAssignAgentForm({...assignAgentForm, masterPartnerId: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un Master Partner" />
                </SelectTrigger>
                <SelectContent>
                  {masterPartnersList.map(mp => (
                    <SelectItem key={mp.user?.id} value={mp.user?.id}>
                      {mp.user?.name} - {mp.region} ({mp.assigned_agents_count} agentes)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {masterPartnersList.length === 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                No hay Master Partners disponibles. Primero debes crear uno.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignAgentModal(false)}>Cancelar</Button>
            <Button 
              onClick={assignAgentToMasterPartner} 
              disabled={actionLoading || !assignAgentForm.masterPartnerId} 
              className="bg-purple-500 hover:bg-purple-600"
            >
              {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
              Asignar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Details Modal - Enhanced Version */}
      <Dialog open={showUserDetailsModal} onOpenChange={setShowUserDetailsModal}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" /> Ficha de Usuario
            </DialogTitle>
          </DialogHeader>
          {selectedUserDetails && (
            <div className="space-y-6 py-4">
              {/* User Header */}
              <div className="flex items-start justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-4">
                  {selectedUserDetails.profile?.agent_photo ? (
                    <img 
                      src={selectedUserDetails.profile.agent_photo} 
                      alt={selectedUserDetails.user?.name}
                      className="w-20 h-20 rounded-full object-cover border-4 border-white shadow"
                    />
                  ) : (
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold ${
                      selectedUserDetails.user?.role === "agent" ? "bg-purple-100 text-purple-600" :
                      selectedUserDetails.user?.role === "owner" ? "bg-green-100 text-green-600" : 
                      selectedUserDetails.user?.role === "shopper" ? "bg-blue-100 text-blue-600" : "bg-slate-100 text-slate-600"
                    }`}>
                      {selectedUserDetails.user?.name?.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <h3 className="text-xl font-bold text-slate-800">{selectedUserDetails.user?.name}</h3>
                    <p className="text-slate-500">{selectedUserDetails.user?.email}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className="capitalize">
                        {selectedUserDetails.user?.role === "shopper" ? "Comprador" : 
                         selectedUserDetails.user?.role === "owner" ? "Propietario" :
                         selectedUserDetails.user?.role === "agent" ? "Agente" : "Administrador"}
                      </Badge>
                      {selectedUserDetails.membership_info?.is_master_partner && (
                        <Badge className="bg-amber-500"><Award className="w-3 h-3 mr-1" /> Master Partner</Badge>
                      )}
                      {selectedUserDetails.user?.status === "disabled" && (
                        <Badge variant="destructive"><Ban className="w-3 h-3 mr-1" /> Deshabilitado</Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right text-sm text-slate-500">
                  <p className="flex items-center gap-1 justify-end">
                    <Calendar className="w-4 h-4" /> Registrado: {new Date(selectedUserDetails.user?.created_at).toLocaleDateString('es-MX')}
                  </p>
                </div>
              </div>

              {/* Membership Info - For Agents */}
              {selectedUserDetails.membership_info && selectedUserDetails.user?.role === "agent" && (
                <Card className="border-l-4 border-l-primary">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Shield className="w-5 h-5 text-primary" /> Estado de Membresía
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <Label className="text-xs text-slate-500">Plan</Label>
                        <p className="font-semibold capitalize">{selectedUserDetails.membership_info.plan || "Freemium"}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <Label className="text-xs text-slate-500">Estatus</Label>
                        <Badge 
                          variant={
                            selectedUserDetails.membership_info.status === "active" ? "default" :
                            selectedUserDetails.membership_info.status === "grace_period" ? "secondary" :
                            selectedUserDetails.membership_info.status === "inactive" ? "destructive" : "outline"
                          }
                          className="mt-1"
                        >
                          {selectedUserDetails.membership_info.status === "active" ? "Activo" :
                           selectedUserDetails.membership_info.status === "pending_approval" ? "Pendiente Aprobación" :
                           selectedUserDetails.membership_info.status === "grace_period" ? "Periodo de Gracia" :
                           selectedUserDetails.membership_info.status === "inactive" ? "Inactivo" :
                           selectedUserDetails.membership_info.status === "expiring_soon" ? "Por Vencer" : 
                           selectedUserDetails.membership_info.status}
                        </Badge>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <Label className="text-xs text-slate-500">Fecha Activación</Label>
                        <p className="font-medium text-sm">
                          {selectedUserDetails.membership_info.activated_at 
                            ? new Date(selectedUserDetails.membership_info.activated_at).toLocaleDateString('es-MX')
                            : "N/A"}
                        </p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <Label className="text-xs text-slate-500">Fecha Vencimiento</Label>
                        <p className="font-medium text-sm">
                          {selectedUserDetails.membership_info.expires_at 
                            ? new Date(selectedUserDetails.membership_info.expires_at).toLocaleDateString('es-MX')
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Master Partner Info - If assigned to one */}
              {selectedUserDetails.master_partner_info && (
                <Card className="border-l-4 border-l-amber-500">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Link2 className="w-5 h-5 text-amber-500" /> Asignado a Master Partner
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 p-3 bg-amber-50 rounded-lg">
                      <Award className="w-8 h-8 text-amber-500" />
                      <div>
                        <p className="font-semibold">{selectedUserDetails.master_partner_info.user?.name}</p>
                        <p className="text-sm text-slate-500">{selectedUserDetails.master_partner_info.user?.email}</p>
                        <p className="text-sm text-amber-600">Región: {selectedUserDetails.master_partner_info.region}</p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="ml-auto"
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/admin/users/${selectedUserDetails.user?.id}/unassign-from-master-partner`);
                            toast.success("Agente desasignado del Master Partner");
                            viewUserDetails(selectedUserDetails.user?.id);
                          } catch (error) {
                            toast.error("Error al desasignar agente");
                          }
                        }}
                      >
                        Desasignar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Assigned Agents - If is Master Partner */}
              {selectedUserDetails.assigned_agents && selectedUserDetails.assigned_agents.length > 0 && (
                <Card className="border-l-4 border-l-purple-500">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="w-5 h-5 text-purple-500" /> Agentes Asignados ({selectedUserDetails.assigned_agents.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {selectedUserDetails.assigned_agents.map((agent) => (
                        <div key={agent.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                              {agent.name?.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium">{agent.name}</p>
                              <p className="text-sm text-slate-500">{agent.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge variant="outline">{agent.properties_count} propiedades</Badge>
                            <Button variant="ghost" size="sm" onClick={() => viewUserDetails(agent.id)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Profile Info */}
              {selectedUserDetails.profile && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Información de Perfil</CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {selectedUserDetails.profile.agency_name && (
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <Label className="text-xs text-slate-500">Agencia</Label>
                        <p className="font-medium">{selectedUserDetails.profile.agency_name}</p>
                      </div>
                    )}
                    {selectedUserDetails.profile.municipality && (
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <Label className="text-xs text-slate-500">Ciudad/Municipio</Label>
                        <p className="font-medium">{selectedUserDetails.profile.municipality}</p>
                      </div>
                    )}
                    {selectedUserDetails.profile.phone && (
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <Label className="text-xs text-slate-500">Teléfono</Label>
                        <p className="font-medium">{selectedUserDetails.profile.phone}</p>
                      </div>
                    )}
                    {selectedUserDetails.profile.authorized_region && (
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <Label className="text-xs text-slate-500">Región Autorizada</Label>
                        <p className="font-medium">{selectedUserDetails.profile.authorized_region}</p>
                      </div>
                    )}
                    {selectedUserDetails.profile.bio && (
                      <div className="p-3 bg-slate-50 rounded-lg col-span-2">
                        <Label className="text-xs text-slate-500">Biografía</Label>
                        <p className="font-medium text-sm">{selectedUserDetails.profile.bio}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Statistics */}
              {selectedUserDetails.statistics && Object.keys(selectedUserDetails.statistics).length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BarChart3 className="w-5 h-5" /> Estadísticas
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(selectedUserDetails.statistics).map(([key, value]) => (
                        <div key={key} className="text-center p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg">
                          <p className="text-3xl font-bold text-primary">
                            {typeof value === "boolean" ? (value ? "Sí" : "No") : value}
                          </p>
                          <p className="text-xs text-slate-500 capitalize mt-1">
                            {key.replace(/_/g, " ").replace("avg", "Promedio").replace("total", "Total")}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Properties List - For Agents */}
              {selectedUserDetails.properties && selectedUserDetails.properties.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Building className="w-5 h-5" /> Propiedades ({selectedUserDetails.properties.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[300px]">
                      <div className="space-y-2">
                        {selectedUserDetails.properties.map((prop) => (
                          <div 
                            key={prop.id} 
                            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer"
                            onClick={() => viewPropertyDetails(prop.id)}
                          >
                            <div className="flex items-center gap-3">
                              <img 
                                src={prop.images?.[0] || "https://via.placeholder.com/60"} 
                                alt={prop.title}
                                className="w-16 h-12 object-cover rounded-lg"
                              />
                              <div>
                                <p className="font-medium">{prop.title}</p>
                                <p className="text-sm text-slate-500 flex items-center gap-1">
                                  <MapPin className="w-3 h-3" /> {prop.municipality}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge variant={prop.status === "active" ? "default" : prop.status === "pending_approval" ? "secondary" : "outline"}>
                                {prop.status === "active" ? "Activa" : prop.status === "pending_approval" ? "Pendiente" : prop.status}
                              </Badge>
                              {prop.features?.price && (
                                <span className="font-bold text-primary">${(prop.features.price / 1000000).toFixed(1)}M</span>
                              )}
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}

              {/* Actions Bar */}
              <div className="flex flex-wrap gap-2 justify-end border-t pt-4">
                {/* Subscription - Only for agents */}
                {selectedUserDetails.user?.role === "agent" && (
                  <Button variant="outline" onClick={() => openSubscriptionModal(selectedUserDetails.user?.id)}>
                    <Zap className="w-4 h-4 mr-2" /> Activar Suscripción
                  </Button>
                )}
                
                {/* Master Partner Actions - Only for agents */}
                {selectedUserDetails.user?.role === "agent" && (
                  <>
                    {selectedUserDetails.membership_info?.is_master_partner ? (
                      <Button 
                        variant="outline"
                        className="text-amber-600 border-amber-300 hover:bg-amber-50"
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/admin/users/${selectedUserDetails.user?.id}/remove-master-partner`);
                            toast.success("Estatus de Master Partner removido");
                            viewUserDetails(selectedUserDetails.user?.id);
                            fetchAllData();
                            fetchMasterPartners();
                          } catch (error) {
                            toast.error("Error al remover Master Partner");
                          }
                        }}
                      >
                        <Award className="w-4 h-4 mr-2" /> Quitar Master Partner
                      </Button>
                    ) : (
                      <>
                        <Button 
                          variant="outline"
                          className="text-amber-600 border-amber-300 hover:bg-amber-50"
                          onClick={() => {
                            setMasterPartnerForm({ userId: selectedUserDetails.user?.id, region: "" });
                            setShowMasterPartnerModal(true);
                          }}
                        >
                          <Award className="w-4 h-4 mr-2" /> Hacer Master Partner
                        </Button>
                        {/* Assign to Master Partner - only if not already assigned */}
                        {!selectedUserDetails.master_partner_info && masterPartnersList.length > 0 && (
                          <Button 
                            variant="outline"
                            className="text-purple-600 border-purple-300 hover:bg-purple-50"
                            onClick={() => {
                              setAssignAgentForm({ agentId: selectedUserDetails.user?.id, masterPartnerId: "" });
                              setShowAssignAgentModal(true);
                            }}
                          >
                            <Link2 className="w-4 h-4 mr-2" /> Asignar a Master Partner
                          </Button>
                        )}
                      </>
                    )}
                  </>
                )}
                
                {/* Enable/Disable */}
                {selectedUserDetails.user?.role !== "admin" && (
                  <>
                    {selectedUserDetails.user?.status === "disabled" ? (
                      <Button 
                        variant="outline" 
                        className="text-green-600 border-green-300 hover:bg-green-50"
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/admin/users/${selectedUserDetails.user?.id}/enable`);
                            toast.success("Usuario habilitado");
                            viewUserDetails(selectedUserDetails.user?.id);
                            fetchAllData();
                          } catch (error) {
                            toast.error("Error al habilitar usuario");
                          }
                        }}
                      >
                        <UserCheck className="w-4 h-4 mr-2" /> Habilitar
                      </Button>
                    ) : (
                      <Button 
                        variant="outline"
                        className="text-orange-600 border-orange-300 hover:bg-orange-50"
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/admin/users/${selectedUserDetails.user?.id}/disable`);
                            toast.success("Usuario deshabilitado");
                            viewUserDetails(selectedUserDetails.user?.id);
                            fetchAllData();
                          } catch (error) {
                            toast.error("Error al deshabilitar usuario");
                          }
                        }}
                      >
                        <UserX className="w-4 h-4 mr-2" /> Deshabilitar
                      </Button>
                    )}
                  </>
                )}
                
                {/* Edit */}
                <Button variant="outline" onClick={() => {
                  setSelectedUser(selectedUserDetails.user);
                  setEditForm({
                    name: selectedUserDetails.user?.name || "",
                    email: selectedUserDetails.user?.email || "",
                    phone_whatsapp: selectedUserDetails.profile?.phone || "",
                    search_city: selectedUserDetails.user?.search_city || "",
                    role: selectedUserDetails.user?.role || ""
                  });
                  setShowUserDetailsModal(false);
                  setShowEditUser(true);
                }}>
                  <Edit className="w-4 h-4 mr-2" /> Editar
                </Button>

                {/* Reset Password */}
                {selectedUserDetails.user?.id && (
                  <Button
                    variant="outline"
                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                    onClick={() => {
                      const u = selectedUserDetails.user;
                      setShowUserDetailsModal(false);
                      openResetPassword(u);
                    }}
                    data-testid={`reset-password-${selectedUserDetails.user?.id}`}
                  >
                    <Key className="w-4 h-4 mr-2" /> Restablecer contraseña
                  </Button>
                )}
                
                {/* Delete - Not for admins */}
                {selectedUserDetails.user?.role !== "admin" && (
                  <Button 
                    variant="destructive"
                    onClick={() => {
                      setSelectedUser(selectedUserDetails.user);
                      setShowUserDetailsModal(false);
                      setShowDeleteConfirm(true);
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Property Details Modal - Using new reusable component */}
      <PropertyDetailModal
        open={showPropertyDetailsModal}
        onOpenChange={setShowPropertyDetailsModal}
        property={selectedPropertyDetails?.property}
        owner={selectedPropertyDetails?.owner}
        agent={selectedPropertyDetails?.agent}
        statistics={selectedPropertyDetails?.statistics}
        canEdit={true}
        canDelete={true}
        canApprove={true}
        onApprove={(id) => {
          approveProperty(id);
          setShowPropertyDetailsModal(false);
        }}
        onReject={(id) => {
          rejectProperty(id);
          setShowPropertyDetailsModal(false);
        }}
        onDelete={async (id) => {
          await deleteProperty(id);
          setShowPropertyDetailsModal(false);
        }}
        onUpdate={() => {
          // Refresh the property details
          if (selectedPropertyDetails?.property?.id) {
            viewPropertyDetails(selectedPropertyDetails.property.id);
          }
          fetchAllData();
        }}
      />

      {/* Reset Password Modal */}
      <Dialog open={showResetPasswordModal} onOpenChange={(open) => {
        setShowResetPasswordModal(open);
        if (!open) {
          setResetPasswordResult(null);
          setResetTargetUser(null);
        }
      }}>
        <DialogContent className="sm:max-w-md" data-testid="reset-password-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-600" />
              {resetPasswordResult ? "Contraseña restablecida" : "Restablecer contraseña"}
            </DialogTitle>
            <DialogDescription>
              {resetPasswordResult
                ? "Comparte la contraseña temporal con el usuario. Por seguridad, recomiéndale cambiarla al ingresar."
                : `Se generará una contraseña temporal para ${resetTargetUser?.name || resetTargetUser?.email}. La contraseña actual quedará invalidada y se enviará un email al usuario.`}
            </DialogDescription>
          </DialogHeader>

          {!resetPasswordResult ? (
            <div className="space-y-4 py-2">
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                <strong>⚠️ Atención:</strong> Esta acción invalida la contraseña actual del usuario. No se puede deshacer.
              </div>
              <div className="text-sm text-slate-600">
                <p><strong>Usuario:</strong> {resetTargetUser?.name}</p>
                <p><strong>Email:</strong> {resetTargetUser?.email}</p>
                <p><strong>Rol:</strong> {resetTargetUser?.role}</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="custom-temp-password" className="text-xs uppercase tracking-widest text-slate-500">
                  Contraseña temporal (opcional)
                </Label>
                <Input
                  id="custom-temp-password"
                  type="text"
                  autoComplete="off"
                  placeholder="Deja vacío para auto-generar (ej: mty@2026)"
                  value={customResetPassword}
                  onChange={(e) => setCustomResetPassword(e.target.value)}
                  data-testid="custom-temp-password-input"
                  minLength={6}
                  maxLength={72}
                />
                <p className="text-[11px] text-slate-500">
                  Si defines una contraseña, se usará esa. Si la dejas vacía, el sistema genera una aleatoria segura. Mín. 6, máx. 72 caracteres.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="p-4 rounded-lg border-2 border-dashed border-primary bg-slate-50 text-center">
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-1">Contraseña temporal</p>
                <p className="font-mono text-2xl font-bold tracking-widest text-[#384451]" data-testid="reset-temp-password">
                  {resetPasswordResult.temp_password}
                </p>
              </div>
              <Button
                variant="outline"
                onClick={copyTempPassword}
                className="w-full"
                data-testid="copy-temp-password-btn"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copiar contraseña
              </Button>
              {resetPasswordResult.email_status === "sent" ? (
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  Email enviado a <strong>{resetPasswordResult.user_email}</strong>
                </div>
              ) : (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                  <strong>📩 Email no enviado.</strong> Por favor comparte la contraseña manualmente con <strong>{resetPasswordResult.user_email}</strong>.
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {!resetPasswordResult ? (
              <>
                <Button variant="outline" onClick={() => setShowResetPasswordModal(false)} disabled={resetting}>
                  Cancelar
                </Button>
                <Button
                  onClick={confirmResetPassword}
                  disabled={resetting}
                  className="bg-blue-600 text-white hover:bg-blue-700"
                  data-testid="confirm-reset-password-btn"
                >
                  {resetting ? "Restableciendo..." : "Confirmar y generar contraseña"}
                </Button>
              </>
            ) : (
              <Button onClick={() => setShowResetPasswordModal(false)} className="bg-primary text-[#384451]" data-testid="close-reset-password-btn">
                Cerrar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
